import React from 'react';
import './Results.css';
import { DailyState, PersistentState } from '../types/GameTypes';
import { SHARING_PLATFORMS } from '../config/sharing';
import { formatDateForResults } from '../utils/dateUtils';
import { StateManagerService } from '../services/StateManagerService';
import { SHARING_PLATFORMS_ANALYTICS } from '../types/AnalyticsConstants';

interface ResultsProps {
  isOpen: boolean;
  dailyState: DailyState;
  persistentState: PersistentState;
  darkMode: boolean;
  onClose: () => void;
  stateManager: StateManagerService;
}

const Results: React.FC<ResultsProps> = ({
  isOpen,
  dailyState,
  persistentState,
  darkMode,
  onClose,
  stateManager
}) => {
  // Use centralized date formatting from dateUtils
  const formatDate = formatDateForResults;


  // RSTLNE letters that should be filtered out from guessed letters display
  const rstlneLetters = ['R', 'S', 'T', 'L', 'N', 'E'];

  // Helper function to filter out RSTLNE letters from guesses
  const filterOutRSTLNELetters = (guesses: string[]): string[] => {
    return guesses.filter(letter => !rstlneLetters.includes(letter));
  };

  // Handle sharing results - copy to clipboard
  const handleCopyResults = () => {
    const clipboard = SHARING_PLATFORMS.clipboard;
    const message = clipboard.generateMessage(dailyState, persistentState);
    
    navigator.clipboard.writeText(message)
      .then(() => {
        // Track successful clipboard share
        stateManager.trackShare(
          SHARING_PLATFORMS_ANALYTICS.CLIPBOARD,
          dailyState.totalScore,
          persistentState.streak,
          true
        );
        alert('Results copied to clipboard!');
      })
      .catch(() => {
        // Track failed clipboard share
        stateManager.trackShare(
          SHARING_PLATFORMS_ANALYTICS.CLIPBOARD,
          dailyState.totalScore,
          persistentState.streak,
          false
        );
        alert('Failed to copy results. Please try again.');
      });
  };

  // Handle tweet sharing
  const handleTweetResults = () => {
    const twitter = SHARING_PLATFORMS.twitter;
    const message = twitter.generateMessage(dailyState, persistentState);
    const url = twitter.generateUrl(message);
    
    // Track Twitter share (assume successful since window.open doesn't provide feedback)
    stateManager.trackShare(
      SHARING_PLATFORMS_ANALYTICS.TWITTER,
      dailyState.totalScore,
      persistentState.streak,
      true
    );
    
    window.open(url, '_blank');
  };

  // Handle Facebook sharing
  const handleFacebookShare = () => {
    const facebook = SHARING_PLATFORMS.facebook;
    const message = facebook.generateMessage(dailyState, persistentState);
    
    // Copy message to clipboard first
    navigator.clipboard.writeText(message)
      .then(() => {
        // Track successful Facebook share
        stateManager.trackShare(
          SHARING_PLATFORMS_ANALYTICS.FACEBOOK,
          dailyState.totalScore,
          persistentState.streak,
          true
        );
        
        // Show user instruction immediately
        alert('Results copied to clipboard! Facebook will open - paste your results in the share dialog.');
        // Then open Facebook sharer
        const url = facebook.generateUrl();
        window.open(url, '_blank');
      })
      .catch(() => {
        // Track Facebook share attempt (clipboard failed but still opening)
        stateManager.trackShare(
          SHARING_PLATFORMS_ANALYTICS.FACEBOOK,
          dailyState.totalScore,
          persistentState.streak,
          false
        );
        
        // Fallback: just open Facebook without copy
        alert('Opening Facebook sharing. You can manually copy your results from the game.');
        const url = facebook.generateUrl();
        window.open(url, '_blank');
      });
  };

  // Handle WhatsApp sharing
  const handleWhatsAppShare = () => {
    const whatsapp = SHARING_PLATFORMS.whatsapp;
    const message = whatsapp.generateMessage(dailyState, persistentState);
    const url = whatsapp.generateUrl(message);
    
    // Track WhatsApp share (assume successful since window.open doesn't provide feedback)
    stateManager.trackShare(
      SHARING_PLATFORMS_ANALYTICS.WHATSAPP,
      dailyState.totalScore,
      persistentState.streak,
      true
    );
    
    window.open(url, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="results-modal" onClick={onClose}>
      <div className="results-content" onClick={(e) => e.stopPropagation()}>
        <div className="results-header">
          <h2>Daily Challenge Complete!</h2>
          <p className="challenge-date">{formatDate(dailyState.date)}</p>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="score-and-streak-info">
          <div className="todays-score">
            <span className="score-label">Today's Score:</span>
            <span className="score-value">{dailyState.totalScore} points</span>
          </div>
          <div className="streak-current">
            <span className="streak-label">Current Streak:</span>
            <span className="streak-value">{persistentState.streak} day{persistentState.streak !== 1 ? 's' : ''}</span>
          </div>
          <div className="streak-total">
            <span className="streak-label">Streak Score:</span>
            <span className="streak-value">{persistentState.streakScore}</span>
          </div>
        </div>

        <div className="results-scrollable-content">
          <div className="word-results">
            {dailyState.results.map((result, index) => (
              <div key={index} className="word-result-container">
                <div className="word-result">
                  <div className="word-info">
                    <span className="word">{result.word}</span>
                  </div>
                  {/* <div className="word-details">
                    <span className="length">{result.length} letters</span>
                    <span className="status">{result.success ? 'Solved' : 'Failed'}</span>
                  </div> */}
                  <span className={`word-score ${result.success ? 'success' : 'failure'}`}>
                    {result.score}
                  </span>
                </div>
                <div className="result-guessed-letters">
                  {filterOutRSTLNELetters(result.letterGuesses).map((letter, letterIndex) => {
                    const isCorrect = result.word.includes(letter);
                    return (
                      <span 
                        key={letterIndex} 
                        className={`result-guessed-letter ${isCorrect ? 'correct' : 'incorrect'}`}
                      >
                        {letter}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="results-footer">
          <div className="share-section">
            <div className="share-buttons">
              <button className="share-icon-button" onClick={handleCopyResults} title="Copy Results">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
              <button className="share-icon-button" onClick={handleTweetResults} title="Tweet">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                </svg>
              </button>
              <button className="share-icon-button facebook" onClick={handleFacebookShare} title="Share on Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </button>
              <button className="share-icon-button whatsapp" onClick={handleWhatsAppShare} title="Share via WhatsApp">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.051 3.488"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;
